/**
 * Pages of the one canonical public documentation that this application links to. The identifiers are the
 * Docs Site page ids; they are the same in every language.
 */
export type Page =
	| 'cdn'
	| 'cdn/quickstart'
	| 'cdn/management/overview-and-auth'
	| 'cdn/management/applications-and-targets'
	| 'cdn/management/registration-and-graph'
	| 'cdn/management/preparation-and-inventory'
	| 'cdn/management/jobs'
	| 'cdn/management/releases-promotion-rollback'
	| 'cdn/management/domains'
	| 'cdn/management/access-and-guests'
	| 'cdn/management/plans-credit-usage'
	| 'cdn/management/quotas'
	| 'cdn/realtime/reconnect-and-replay'
	| 'cdn/realtime/revocation'
	| 'cdn/delivery/caching-and-releases'
	| 'cdn/delivery/private-access'
	| 'cdn/delivery/retrieval'
	| 'cdn/delivery/loaders-esm-importmap'
	| 'cdn/delivery/errors'
	| 'cdn/guides/backend-targets';

interface Origins {
	docs: string;
	landing: string;
	accounts?: string;
	projects?: string;
	workspace?: string;
	packages?: string;
}

/**
 * Builds links to the public Docs Site, to Beyond Landing in the language of the interface, to the Beyond
 * Accounts interface where organizations are managed, to Beyond Projects, and back to the product that opened
 * a page. This application holds no marketing or reference content of its own, and no organization of its own.
 */
export class Docs {
	/** The products a way back can lead to: the ones this deployment may name an address of */
	static #families = ['projects', 'accounts', 'workspace', 'packages'];

	#origins: Origins;

	constructor(origins: Origins) {
		this.#origins = origins;
	}

	/**
	 * The way back to a product of the family that opened a page of CDN (`?from=<product>`, decision D54), built
	 * from this deployment's own configuration and never from the link: Beyond Projects reopened on the project
	 * or the organization in view, Beyond Accounts on the organization, Workspace at its start, Packages on the project
	 * a publication task was opened for.
	 *
	 * @returns undefined for a product this deployment names no address of: no way back is drawn
	 */
	back(product: string, place: { project?: string; organization?: string } = {}): string | undefined {
		const { project, organization } = place;
		const origin = Docs.#families.includes(product) ? this.#origins[product as keyof Origins] : undefined;
		if (!origin) return undefined;
		const url = new URL(`${origin}/`);
		if ((product === 'projects' || product === 'packages') && project) url.searchParams.set('project', project);
		else if (product === 'projects' && organization) url.searchParams.set('organization', organization);
		else if (product === 'accounts' && organization) url.pathname = `/organizations/${encodeURIComponent(organization)}`;
		return url.toString();
	}

	/**
	 * Beyond Projects, the family's home, when this deployment names it: where the family bar's wordmark leads
	 * while the bar has no descriptor
	 */
	get home(): string | undefined {
		return this.#origins.projects ? `${this.#origins.projects}/` : undefined;
	}

	/**
	 * Where a person creates or manages organizations, when this deployment names the Accounts interface
	 */
	get organizations(): string | undefined {
		return this.#origins.accounts ? `${this.#origins.accounts}/organizations` : undefined;
	}

	/**
	 * The person's own page in the Beyond Accounts interface, when this deployment names it
	 */
	get account(): string | undefined {
		return this.#origins.accounts ? `${this.#origins.accounts}/account` : undefined;
	}

	/**
	 * The Accounts pages of the family bar's profile menu while it has no descriptor (`links.manage` of
	 * `beyond-family/1`), when this deployment names the Accounts interface. The bar adds `product` and `return`
	 * itself; the members page is for the organization in view, and Accounts decides what the person may do there.
	 */
	manage(organization?: string): { account: string; organizations: string; create: string; members?: string } | undefined {
		const accounts = this.#origins.accounts;
		if (!accounts) return undefined;
		const members = organization ? `${accounts}/organizations/${encodeURIComponent(organization)}?view=members` : undefined;
		return { account: `${accounts}/account`, organizations: `${accounts}/organizations`, create: `${accounts}/organizations?view=create`, ...(members && { members }) };
	}

	/**
	 * "Change for all of Beyond" (decision D07): the person's page in the Accounts interface, where their language
	 * and appearance are changed for every product, with the way back to `returning`, which Accounts offers only
	 * after checking it is one of CDN's own addresses
	 */
	everywhere(returning: string): string | undefined {
		if (!this.#origins.accounts) return undefined;
		const url = new URL(`${this.#origins.accounts}/account`);
		url.searchParams.set('product', 'cdn');
		url.searchParams.set('return', returning);
		return url.toString();
	}

	/**
	 * Accounts' `/leave`, with no query, when this deployment names the Accounts interface: the family bar's
	 * fallback for the descriptor's `links.leave`, which it completes with `product` and `return` itself
	 */
	get departure(): string | undefined {
		return this.#origins.accounts ? `${this.#origins.accounts}/leave` : undefined;
	}

	/**
	 * "Sign out of Beyond" (Q09): Accounts' `/leave` with this product and the way back to `returning`, which
	 * Accounts offers only after checking it is one of CDN's own addresses. `address` is the descriptor's
	 * `links.leave` when Projects answered one; otherwise the Accounts interface this deployment names.
	 *
	 * @returns undefined when neither is known: the person then signs out of CDN only
	 */
	leave(returning: string, address?: string): string | undefined {
		const base = address ?? this.departure;
		if (!base) return undefined;
		const url = new URL(base);
		url.searchParams.set('product', 'cdn');
		url.searchParams.set('return', returning);
		return url.toString();
	}

	/**
	 * @returns `<docs origin>/<locale>/<page>/`
	 */
	page(id: Page, locale: string): string {
		return `${this.#origins.docs}/${locale}/${id}/`;
	}

	/**
	 * The CDN presentation inside Beyond Landing
	 */
	landing(locale: string): string {
		return `${this.#origins.landing}/${locale}/cdn/`;
	}
}
